import { modelOptions, prop as Prop } from '@typegoose/typegoose'
import { ObjectType, Field, Int, InputType, ID, registerEnumType } from 'type-graphql'
import type { MetadataType } from '../base'
import { BaseEntity, Metadata } from '../base'
import { generateEntityId } from '../generateEntityId'
import GraphQLJSON from 'graphql-type-json'

// ================== Enums ==================
export enum PaymentIntentStatus {
  requires_payment_method = 'requires_payment_method',
  requires_confirmation = 'requires_confirmation',
  requires_action = 'requires_action',
  processing = 'processing',
  requires_capture = 'requires_capture',
  canceled = 'canceled',
  succeeded = 'succeeded',
}

export enum PaymentIntentCaptureMethod {
  automatic = 'automatic',
  automatic_async = 'automatic_async',
  manual = 'manual',
}

export enum PaymentIntentConfirmationMethod {
  automatic = 'automatic',
  manual = 'manual',
}

registerEnumType(PaymentIntentStatus, {
  name: 'PaymentIntentStatus',
  description: 'Status of a Stripe Payment Intent',
})

registerEnumType(PaymentIntentCaptureMethod, {
  name: 'PaymentIntentCaptureMethod',
  description: 'Capture method for a Stripe Payment Intent',
})

registerEnumType(PaymentIntentConfirmationMethod, {
  name: 'PaymentIntentConfirmationMethod',
  description: 'Confirmation method for a Stripe Payment Intent',
})

// ================== Nested Types ==================
@ObjectType('PaymentIntentTransferData')
@InputType('PaymentIntentTransferDataInput')
class TransferData {
  @Field(() => ID)
  @Prop({ type: String, required: true, default: () => generateEntityId('ent') })
  _id: string

  @Field(() => String, { nullable: true })
  @Prop({ type: String })
  destination?: string

  @Field(() => Int, { nullable: true })
  @Prop({ type: Number })
  amount?: number
}

@ObjectType('PaymentIntentAddress')
@InputType('PaymentIntentAddressInput')
class Address {
  @Field(() => ID)
  @Prop({ type: String, required: true, default: () => generateEntityId('ent') })
  _id: string

  @Field(() => String)
  @Prop({ type: String })
  city?: string

  @Field(() => String)
  @Prop({ type: String })
  country?: string

  @Field(() => String)
  @Prop({ type: String })
  line1?: string

  @Field(() => String)
  @Prop({ type: String })
  line2?: string

  @Field(() => String)
  @Prop({ type: String })
  postal_code?: string

  @Field(() => String)
  @Prop({ type: String })
  state?: string
}

@ObjectType('PaymentIntentBillingDetails')
@InputType('PaymentIntentBillingDetailsInput')
class BillingDetails {
  @Field(() => ID)
  @Prop({ type: String, required: true, default: () => generateEntityId('ent') })
  _id: string

  @Field(() => String)
  @Prop({ type: String })
  name?: string

  @Field(() => String)
  @Prop({ type: String })
  email?: string

  @Field(() => String)
  @Prop({ type: String })
  phone?: string

  @Field(() => Address)
  @Prop({ type: () => Address })
  address?: Address

  @Field(() => String)
  @Prop({ type: String })
  tax_id?: string
}

@ObjectType('PaymentIntentNetworkToken')
@InputType('PaymentIntentNetworkTokenInput')
class NetworkToken {
  @Field(() => ID)
  @Prop({ type: String, required: true, default: () => generateEntityId('ent') })
  _id: string

  @Field(() => Boolean)
  @Prop({ type: Boolean })
  used?: boolean
}

@ObjectType('PaymentIntentCardDetails')
@InputType('PaymentIntentCardDetailsInput')
class CardDetails {
  @Field(() => ID)
  @Prop({ type: String, required: true, default: () => generateEntityId('ent') })
  _id: string

  @Field(() => Int)
  @Prop({ type: Number })
  amount_authorized?: number

  @Field(() => String)
  @Prop({ type: String })
  authorization_code?: string

  @Field(() => String)
  @Prop({ type: String })
  brand?: string

  @Field(() => String)
  @Prop({ type: String })
  country?: string

  @Field(() => Int)
  @Prop({ type: Number })
  exp_month?: number

  @Field(() => Int)
  @Prop({ type: Number })
  exp_year?: number

  @Field(() => String)
  @Prop({ type: String })
  fingerprint?: string

  @Field(() => String)
  @Prop({ type: String })
  funding?: string

  @Field(() => String)
  @Prop({ type: String })
  last4?: string

  @Field(() => String)
  @Prop({ type: String })
  network?: string

  @Field(() => NetworkToken)
  @Prop({ type: () => NetworkToken })
  network_token?: NetworkToken
}

@ObjectType('PaymentIntentPaymentMethodDetails')
@InputType('PaymentIntentPaymentMethodDetailsInput')
class PaymentMethodDetails {
  @Field(() => ID)
  @Prop({ type: String, required: true, default: () => generateEntityId('ent') })
  _id: string

  @Field(() => CardDetails)
  @Prop({ type: () => CardDetails })
  card?: CardDetails

  @Field(() => String)
  @Prop({ type: String })
  type?: string
}

@ObjectType('PaymentIntentRefundDestinationCard')
@InputType('PaymentIntentRefundDestinationCardInput')
class RefundDestinationCard {
  @Field(() => ID)
  @Prop({ type: String, required: true, default: () => generateEntityId('ent') })
  _id: string

  @Field(() => String)
  @Prop({ type: String })
  reference?: string

  @Field(() => String)
  @Prop({ type: String })
  reference_status?: string

  @Field(() => String)
  @Prop({ type: String })
  reference_type?: string

  @Field(() => String)
  @Prop({ type: String })
  type?: string
}

@ObjectType('PaymentIntentRefundDestinationDetails')
@InputType('PaymentIntentRefundDestinationDetailsInput')
class RefundDestinationDetails {
  @Field(() => ID)
  @Prop({ type: String, required: true, default: () => generateEntityId('ent') })
  _id: string

  @Field(() => RefundDestinationCard)
  @Prop({ type: () => RefundDestinationCard })
  card?: RefundDestinationCard

  @Field(() => String)
  @Prop({ type: String })
  type?: string
}

@ObjectType('PaymentIntentRefundPresentmentDetails')
@InputType('PaymentIntentRefundPresentmentDetailsInput')
class RefundPresentmentDetails {
  @Field(() => ID)
  @Prop({ type: String, required: true, default: () => generateEntityId('ent') })
  _id: string

  @Field(() => Int)
  @Prop({ type: Number })
  presentment_amount?: number

  @Field(() => String)
  @Prop({ type: String })
  presentment_currency?: string
}

@ObjectType('PaymentIntentRefund')
@InputType('PaymentIntentRefundInput')
class Refund {
  @Field(() => ID)
  @Prop({ type: String, required: true, default: () => generateEntityId('ent') })
  _id: string

  @Field(() => String)
  @Prop({ type: String })
  id: string

  @Field(() => Int)
  @Prop({ type: Number })
  amount: number

  @Field(() => String)
  @Prop({ type: String })
  currency: string

  @Field(() => String)
  @Prop({ type: String })
  status?: string

  @Field(() => RefundDestinationDetails)
  @Prop({ type: () => RefundDestinationDetails })
  destination_details?: RefundDestinationDetails

  @Field(() => RefundPresentmentDetails)
  @Prop({ type: () => RefundPresentmentDetails })
  presentment_details?: RefundPresentmentDetails

  @Field(() => GraphQLJSON)
  @Prop({ type: Metadata })
  metadata?: MetadataType

  @Field(() => String)
  @Prop({ type: String })
  receipt_number?: string

  @Field(() => String)
  @Prop({ type: String })
  source_transfer_reversal?: string

  @Field(() => String)
  @Prop({ type: String })
  transfer_reversal?: string

  @Field(() => Int)
  @Prop({ type: Number })
  created?: number

  @Field(() => String)
  @Prop({ type: String })
  charge?: string

  @Field(() => String)
  @Prop({ type: String })
  balance_transaction?: string

  @Field(() => String)
  @Prop({ type: String })
  reason?: string
}

@ObjectType('PaymentIntentRefundsList')
@InputType('PaymentIntentRefundsListInput')
class RefundsList {
  @Field(() => ID)
  @Prop({ type: String, required: true, default: () => generateEntityId('ent') })
  _id: string

  @Field(() => String)
  @Prop({ type: String })
  object: string

  @Field(() => [Refund])
  @Prop({ type: () => [Refund] })
  data: Refund[]

  @Field(() => Boolean)
  @Prop({ type: Boolean })
  has_more: boolean

  @Field(() => Int)
  @Prop({ type: Number })
  total_count: number

  @Field(() => String)
  @Prop({ type: String })
  url?: string
}

@ObjectType('PaymentIntentChargeOutcome')
@InputType('PaymentIntentChargeOutcomeInput')
class ChargeOutcome {
  @Field(() => ID)
  @Prop({ type: String, required: true, default: () => generateEntityId('ent') })
  _id: string

  @Field(() => String)
  @Prop({ type: String })
  network_status?: string

  @Field(() => String)
  @Prop({ type: String })
  reason?: string

  @Field(() => String)
  @Prop({ type: String })
  seller_message?: string

  @Field(() => String)
  @Prop({ type: String })
  type?: string

  @Field(() => String)
  @Prop({ type: String })
  advice_code?: string

  @Field(() => String)
  @Prop({ type: String })
  network_advice_code?: string

  @Field(() => String)
  @Prop({ type: String })
  network_decline_code?: string

  @Field(() => String)
  @Prop({ type: String })
  risk_level?: string

  @Field(() => Int)
  @Prop({ type: Number })
  risk_score?: number
}

@ObjectType('PaymentIntentPresentmentDetails')
@InputType('PaymentIntentPresentmentDetailsInput')
class PresentmentDetails {
  @Field(() => ID)
  @Prop({ type: String, required: true, default: () => generateEntityId('ent') })
  _id: string

  @Field(() => Int)
  @Prop({ type: Number })
  presentment_amount?: number

  @Field(() => String)
  @Prop({ type: String })
  presentment_currency?: string
}

@ObjectType('PaymentIntentCharge')
@InputType('PaymentIntentChargeInput')
class Charge {
  @Field(() => ID)
  @Prop({ type: String, required: true, default: () => generateEntityId('chg') })
  _id: string

  @Field(() => String)
  @Prop({ type: String })
  id: string

  @Field(() => String)
  @Prop({ type: String })
  object: string

  @Field(() => Int)
  @Prop({ type: Number })
  amount: number

  @Field(() => Int)
  @Prop({ type: Number })
  amount_captured: number

  @Field(() => Int)
  @Prop({ type: Number })
  amount_refunded: number

  @Field(() => BillingDetails)
  @Prop({ type: () => BillingDetails })
  billing_details?: BillingDetails

  @Field(() => PaymentMethodDetails)
  @Prop({ type: () => PaymentMethodDetails })
  payment_method_details?: PaymentMethodDetails

  @Field(() => Boolean)
  @Prop({ type: () => Boolean })
  refunded?: boolean

  @Field(() => RefundsList)
  @Prop({ type: () => RefundsList })
  refunds?: RefundsList

  @Field(() => ChargeOutcome)
  @Prop({ type: () => ChargeOutcome })
  outcome?: ChargeOutcome

  @Field(() => PresentmentDetails)
  @Prop({ type: () => PresentmentDetails })
  presentment_details?: PresentmentDetails

  @Field(() => Boolean)
  @Prop({ type: Boolean })
  paid?: boolean

  @Field(() => Boolean)
  @Prop({ type: Boolean })
  captured?: boolean

  @Field(() => String)
  @Prop({ type: String })
  status?: string

  @Field(() => GraphQLJSON)
  @Prop({ type: Metadata })
  metadata?: MetadataType

  @Field(() => String)
  @Prop({ type: String })
  application?: string

  @Field(() => String)
  @Prop({ type: String })
  application_fee?: string

  @Field(() => Int)
  @Prop({ type: Number })
  application_fee_amount?: number

  // --- Newly Added Charge Fields ---
  @Field(() => String, { nullable: true })
  @Prop({ type: String })
  transfer_group?: string

  @Field(() => TransferData, { nullable: true })
  @Prop({ type: () => TransferData })
  transfer_data?: TransferData

  @Field(() => String, { nullable: true })
  @Prop({ type: String })
  on_behalf_of?: string

  @Field(() => String, { nullable: true })
  @Prop({ type: String })
  receipt_email?: string
}

@ObjectType('PaymentIntentCharges')
@InputType('PaymentIntentChargesInput')
class PaymentIntentCharges {
  @Field(() => ID)
  @Prop({ type: String, required: true, default: () => generateEntityId('chg') })
  _id: string

  @Field(() => String)
  @Prop({ type: String })
  object: string

  @Field(() => Boolean)
  @Prop({ type: Boolean })
  has_more: boolean

  @Field(() => [Charge])
  @Prop({ type: () => [Charge] })
  data: Charge[]

  @Field(() => Int)
  @Prop({ type: Number })
  total_count: number

  @Field(() => String)
  @Prop({ type: String })
  url?: string
}

// ================== PaymentIntent ==================
@ObjectType()
@InputType('StripePaymentIntentInput')
@modelOptions({ schemaOptions: { timestamps: true } })
export class StripePaymentIntent extends BaseEntity {
  @Field(() => ID)
  @Prop({ type: String, required: true, default: () => generateEntityId('pi') })
  declare _id: string

  @Field(() => String)
  @Prop({ type: String })
  id: string

  @Field(() => String)
  @Prop({ type: String })
  object: string

  @Field(() => Int)
  @Prop({ type: Number })
  amount: number

  @Field(() => Int)
  @Prop({ type: Number })
  amount_capturable: number

  @Field(() => Int)
  @Prop({ type: Number })
  amount_received: number

  @Field(() => PaymentIntentStatus)
  @Prop({ type: String, enum: Object.values(PaymentIntentStatus) })
  declare status?: PaymentIntentStatus

  @Field(() => PaymentIntentCaptureMethod)
  @Prop({ type: String, enum: Object.values(PaymentIntentCaptureMethod) })
  capture_method?: PaymentIntentCaptureMethod

  @Field(() => PaymentIntentConfirmationMethod)
  @Prop({ type: String, enum: Object.values(PaymentIntentConfirmationMethod) })
  confirmation_method?: PaymentIntentConfirmationMethod

  @Field(() => String)
  @Prop({ type: String })
  client_secret?: string

  @Field(() => String)
  @Prop({ type: String })
  currency?: string

  @Field(() => String)
  @Prop({ type: String })
  customer?: string

  @Field(() => BillingDetails)
  @Prop({ type: () => BillingDetails })
  billing_details?: BillingDetails

  @Field(() => PaymentIntentCharges)
  @Prop({ type: () => PaymentIntentCharges })
  charges?: PaymentIntentCharges

  @Field(() => GraphQLJSON)
  @Prop({ type: Metadata })
  metadata?: MetadataType

  // --- Newly Added Root Fields ---
  @Field(() => GraphQLJSON, { nullable: true })
  @Prop({ type: () => Metadata })
  amount_details?: MetadataType

  @Field(() => String, { nullable: true })
  @Prop({ type: String })
  application?: string

  @Field(() => Int, { nullable: true })
  @Prop({ type: Number })
  application_fee_amount?: number

  @Field(() => String, { nullable: true })
  @Prop({ type: String })
  cancellation_reason?: string

  @Field(() => Int, { nullable: true })
  @Prop({ type: Number })
  canceled_at?: number

  @Field(() => String, { nullable: true })
  @Prop({ type: String })
  customer_account?: string

  @Field(() => String, { nullable: true })
  @Prop({ type: String })
  description?: string

  @Field(() => String, { nullable: true })
  @Prop({ type: String })
  latest_charge?: string

  @Field(() => Boolean)
  @Prop({ type: Boolean })
  livemode: boolean

  @Field(() => String, { nullable: true })
  @Prop({ type: String })
  on_behalf_of?: string

  @Field(() => String, { nullable: true })
  @Prop({ type: String })
  payment_method?: string

  @Field(() => [String])
  @Prop({ type: [String] })
  payment_method_types: string[]

  @Field(() => GraphQLJSON, { nullable: true })
  @Prop({ type: Metadata })
  payment_method_options?: MetadataType

  @Field(() => String, { nullable: true })
  @Prop({ type: String })
  receipt_email?: string

  @Field(() => String, { nullable: true })
  @Prop({ type: String })
  setup_future_usage?: string

  @Field(() => String, { nullable: true })
  @Prop({ type: String })
  transfer_group?: string

  @Field(() => TransferData, { nullable: true })
  @Prop({ type: () => TransferData })
  transfer_data?: TransferData

  @Field(() => Int)
  @Prop({ type: Number })
  created: number
}
